// src/utils/errorTranslator.ts
export type ErrorKey =
  | "JOURNAL_BATCH_NOT_FOUND"
  | "DIMENSION_MISSING"
  | "ITEM_BLOCKED_PURCHASING"
  | "INSUFFICIENT_QTY"
  | "MISSING_QTY_IN_LINES"
  | "ITEM_NOT_FOUND"
  | "HTTP_400"
  | "HTTP_500"
  | "GENERIC";

export interface TranslatedError {
  key: ErrorKey;
  message: string;
  correlationId?: string | null;
}

/**
 * Patrones robustos:
 * - Usamos [\s\S]* para permitir saltos de línea y cualquier texto intermedio.
 * - Mantenemos /i (case-insensitive).
 */
const PATTERNS: Array<{ test: RegExp; key: ErrorKey; message: string }> = [
  // “The field Journal Batch Name of table Item Journal Line ... that cannot be found ... (Item Journal Batch).”
  {
    test: /journal\s+batch\s+name[\s\S]*cannot\s+be\s+found[\s\S]*item\s+journal\s+batch/i,
    key: "JOURNAL_BATCH_NOT_FOUND",
    message:
      "No se encontró el lote de diario configurado para esta empresa. Verifica que exista el lote “DEFAULT” (u otro configurado) en Business Central, en 'Líneas de Diario de Ítems', y que esté asociado a la empresa. Informe a soporte si el problema persiste.",
  },

  // “Select a Dimension Value Code …” / “ShortcutDimension…”
  {
    test: /dimension[\s\S]*value[\s\S]*code|shortcutdimension/i,
    key: "DIMENSION_MISSING",
    message:
      "Falta asignar una o más dimensiones requeridas (Centro de Costo, Departamento, Proyecto, etc.).",
  },

  // “Purchasing Blocked…”
  {
    test: /purchasing[\s\S]*blocked/i,
    key: "ITEM_BLOCKED_PURCHASING",
    message:
      "El artículo está bloqueado para compras en Business Central. Debe desbloquearse antes de continuar.",
  },

  // “You have insufficient quantity…”
  {
    test: /insufficient[\s\S]*quantity/i,
    key: "INSUFFICIENT_QTY",
    message:
      "No hay suficiente stock del artículo para realizar la operación.",
  },

  // “You cannot post these lines because you have not entered a quantity…”
  {
    test: /have[\s\S]*not[\s\S]*entered[\s\S]*a[\s\S]*quantity/i,
    key: "MISSING_QTY_IN_LINES",
    message:
      "Falta ingresar la cantidad en una o más líneas del documento.",
  },

  // “The field Item Id … cannot be found in the related table (Item).”
  {
    test: /field[\s\S]*item[\s\S]*id[\s\S]*cannot[\s\S]*be[\s\S]*found[\s\S]*related[\s\S]*table[\s\S]*item/i,
    key: "ITEM_NOT_FOUND",
    message:
      "Uno de los artículos seleccionados no existe o fue eliminado en Business Central.",
  },
];

function extractCorrelationId(text?: string): string | null {
  if (!text) return null;
  const m = text.match(/correlationid:\s*([a-f0-9-]{30,})/i);
  return m?.[1] ?? null;
}

/**
 * Normaliza el “payload” de error desde Axios u otras fuentes.
 * - Lee data.message, data.error.message, data.error
 * - Si hay data.errors (array), concatena sus mensajes
 */
export function normalizeErrorPayload(
  err: unknown
): { raw: string; status?: number } {
  const anyErr = err as any;
  const status: number | undefined = anyErr?.response?.status;
  const data = anyErr?.response?.data;

  // Captura múltiples formatos habituales
  const arrErrors: string[] | null =
    Array.isArray(data?.errors)
      ? data.errors
          .map((e: any) => e?.message ?? e?.detail ?? (typeof e === "string" ? e : null))
          .filter((x: any) => typeof x === "string")
      : null;

  let raw =
    (arrErrors && arrErrors.length ? arrErrors.join("\n• ") : undefined) ??
    data?.message ??
    data?.error?.message ?? // <-- importante: cuando error es objeto
    data?.error ??
    anyErr?.message ??
    String(anyErr);

  if (typeof raw !== "string") raw = JSON.stringify(raw);

  return { raw, status };
}

/** Traduce el error técnico a uno legible para el usuario. */
export function translateBcError(err: unknown): TranslatedError {
  const { raw, status } = normalizeErrorPayload(err);
  const lower = raw.toLowerCase();

  // HTTP primero (por si no hay payload útil)
  if (status === 400) {
    return {
      key: "HTTP_400",
      message:
        "No fue posible procesar los datos enviados. Revisa la información del formulario.",
      correlationId: extractCorrelationId(raw),
    };
  }
  if (status === 500) {
    return {
      key: "HTTP_500",
      message:
        "Business Central rechazó la solicitud por un error interno. Intenta nuevamente o contacta a soporte.",
      correlationId: extractCorrelationId(raw),
    };
  }

  // Patrones conocidos (con [\s\S]* para tolerar saltos de línea y texto extra)
  for (const p of PATTERNS) {
    if (p.test.test(lower)) {
      return {
        key: p.key,
        message: p.message,
        correlationId: extractCorrelationId(raw),
      };
    }
  }

  // Fallback: mostramos explícitamente el mensaje original de BC
  return {
    key: "GENERIC",
    message:raw,
    correlationId: extractCorrelationId(raw),
  };
}
